import {
  Add,
  Bookmark,
  CheckCircle,
  Close,
  NoteAlt,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Song, SongModel } from "../Entities/SongModel";
import { useChordLists } from "../utils/useChordLists";

interface SaveToListModalProps {
  open: boolean;
  onClose: () => void;
  song: Song | SongModel;
}

const LIST_COLORS = [
  "#3b82f6", // Blue
  "#10b981", // Emerald
  "#f59e0b", // Amber
  "#ef4444", // Rose
  "#8b5cf6", // Purple
  "#ec4899", // Pink
  "#06b6d4", // Cyan
  "#64748b", // Slate
];

export default function SaveToListModal({
  open,
  onClose,
  song,
}: SaveToListModalProps) {
  const { lists, loading, createList, addSongToList, deleteListItem, getSongSavedStatus } =
    useChordLists();

  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const [existingItemsByList, setExistingItemsByList] = useState<{
    [listId: number]: number;
  }>({});
  const [notes, setNotes] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [showCreateList, setShowCreateList] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [newListColor, setNewListColor] = useState(LIST_COLORS[0]);
  const [isCreatingList, setIsCreatingList] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

  useEffect(() => {
    if (open && song && song.id) {
      // Load saved status for this song
      getSongSavedStatus(song.id).then((savedInfos) => {
        const itemMap: { [listId: number]: number } = {};
        let initialNote = "";
        let currentListId: number | null = null;
        savedInfos.forEach((s) => {
          itemMap[s.list_id] = s.item_id;
          currentListId = s.list_id;
          if (s.notes && !initialNote) initialNote = s.notes;
        });
        setSelectedListId(currentListId);
        setExistingItemsByList(itemMap);
        setNotes(initialNote);
      });
    }
  }, [open, song, getSongSavedStatus]);

  const handleToggleList = (listId: number) => {
    setSelectedListId((prev) => (prev === listId ? null : listId));
  };

  const handleCreateNewList = async () => {
    if (!newListName.trim()) return;
    setIsCreatingList(true);
    try {
      const created = await createList({
        name: newListName.trim(),
        color: newListColor,
      });
      setSelectedListId(created.id);
      setNewListName("");
      setShowCreateList(false);
    } catch (err) {
      console.error("Failed to create list:", err);
    } finally {
      setIsCreatingList(false);
    }
  };

  const handleSave = async () => {
    if (!song || !song.id) return;
    setIsSaving(true);
    try {
      if (selectedListId !== null) {
        // Add or move to selected list
        await addSongToList(selectedListId, {
          song_id: song.id,
          notes: notes.trim() || undefined,
        });

        // For any previous list different from the target list: remove item
        for (const listId of Object.keys(existingItemsByList).map(Number)) {
          if (listId !== selectedListId) {
            const itemId = existingItemsByList[listId];
            if (itemId) {
              await deleteListItem(itemId);
            }
          }
        }
        setSnackbarMessage("Saved to list successfully!");
      } else {
        // Unselected all lists: delete from previous list
        for (const listId of Object.keys(existingItemsByList).map(Number)) {
          const itemId = existingItemsByList[listId];
          if (itemId) {
            await deleteListItem(itemId);
          }
        }
        setSnackbarMessage("Removed from lists.");
      }

      setTimeout(() => {
        onClose();
      }, 400);
    } catch (err: any) {
      console.error("Error saving song to list:", err);
      setSnackbarMessage(err.message || "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  bgcolor: "primary.light",
                  color: "primary.main",
                  p: 1,
                  borderRadius: 2,
                  display: "flex",
                }}
              >
                <Bookmark />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Save to Chord Lists
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {song.title} {song.artist ? `• ${song.artist}` : ""}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ py: 2.5 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={3}>
              {/* Select Lists Section */}
              <Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1.5}
                >
                  <Typography variant="subtitle2" fontWeight="600" color="text.primary">
                    Select Lists:
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Add fontSize="small" />}
                    onClick={() => setShowCreateList((prev) => !prev)}
                    sx={{ textTransform: "none", fontSize: "0.85rem" }}
                  >
                    {showCreateList ? "Cancel New List" : "New List"}
                  </Button>
                </Box>

                {/* Inline New List Creator */}
                {showCreateList && (
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 2,
                      bgcolor: "grey.50",
                      borderColor: "primary.main",
                      borderStyle: "dashed",
                    }}
                  >
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                      <Typography variant="body2" fontWeight="600" mb={1}>
                        Create New List:
                      </Typography>
                      <Box display="flex" gap={1} mb={1.5}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="e.g., Acoustic Jam, Live Setlist"
                          value={newListName}
                          onChange={(e) => setNewListName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleCreateNewList();
                            }
                          }}
                        />
                        <Button
                          variant="contained"
                          size="small"
                          disabled={!newListName.trim() || isCreatingList}
                          onClick={handleCreateNewList}
                          sx={{ textTransform: "none", minWidth: 90 }}
                        >
                          {isCreatingList ? <CircularProgress size={18} /> : "Create"}
                        </Button>
                      </Box>
                      {/* Color selection */}
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="caption" color="text.secondary">
                          Color:
                        </Typography>
                        {LIST_COLORS.map((col) => (
                          <Box
                            key={col}
                            onClick={() => setNewListColor(col)}
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: "50%",
                              bgcolor: col,
                              cursor: "pointer",
                              border:
                                newListColor === col
                                  ? "2px solid #000"
                                  : "2px solid transparent",
                              transform:
                                newListColor === col ? "scale(1.2)" : "scale(1)",
                              transition: "all 0.15s ease",
                            }}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* List items with single-select choice */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    maxHeight: 200,
                    overflowY: "auto",
                    pr: 0.5,
                  }}
                >
                  {lists.map((list) => {
                    const isChecked = selectedListId === list.id;
                    return (
                      <Box
                        key={list.id}
                        onClick={() => handleToggleList(list.id)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: 1.2,
                          px: 1.8,
                          borderRadius: 2,
                          cursor: "pointer",
                          bgcolor: isChecked ? "primary.50" : "grey.50",
                          border: "1px solid",
                          borderColor: isChecked ? "primary.main" : "grey.200",
                          transition: "all 0.15s ease",
                          "&:hover": {
                            bgcolor: isChecked ? "primary.50" : "grey.100",
                          },
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              bgcolor: list.color || "#3b82f6",
                            }}
                          />
                          <Typography variant="body2" fontWeight="600">
                            {list.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ({list.item_count || list.items?.length || 0} songs)
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            border: "2px solid",
                            borderColor: isChecked ? "primary.main" : "#94a3b8",
                            bgcolor: isChecked ? "primary.main" : "transparent",
                            flexShrink: 0,
                          }}
                        />
                      </Box>
                    );
                  })}
                  {lists.length === 0 && (
                    <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                      No lists found. Click "New List" above to create one.
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Personal Notes Section */}
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <NoteAlt fontSize="small" color="action" />
                  <Typography variant="subtitle2" fontWeight="600" color="text.primary">
                    Personal Song Notes & Annotations:
                  </Typography>
                </Box>
                <TextField
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="e.g., Use capo on 2nd fret; strumming pattern: D-DU-UDU; practice the intro fingerpicking!"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  variant="outlined"
                  sx={{
                    bgcolor: "#fff",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  Notes are saved with this chord sheet across all your lists and Kanban cards.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} color="inherit" sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={isSaving || (selectedListId === null && Object.keys(existingItemsByList).length === 0)}
            startIcon={isSaving ? <CircularProgress size={16} /> : <CheckCircle />}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              background: "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
            }}
          >
            {isSaving ? "Saving..." : selectedListId !== null ? "Save to List" : "Remove from Lists"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={3000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
}
